import { Smartphone } from 'lucide-react';
import mpesaLogo from '../../assets/images/operators/mpesa.webp';
import orangeMoneyLogo from '../../assets/images/operators/orange_money.webp';
import airtelMoneyLogo from '../../assets/images/operators/airtel_money.webp';
import afrimoneyLogo from '../../assets/images/operators/afrimoney.webp';

/**
 * Logos des opérateurs Mobile Money (RDC), source unique de l'app.
 * Tuiles carrées arrondies prêtes à l'emploi : jamais recolorées ni étirées.
 */

export type OperatorKey = 'mpesa' | 'orange_money' | 'airtel_money' | 'afrimoney';

export const OPERATOR_INFO: Record<OperatorKey, { name: string; logo: string }> = {
  mpesa: { name: 'M-Pesa', logo: mpesaLogo },
  orange_money: { name: 'Orange Money', logo: orangeMoneyLogo },
  airtel_money: { name: 'Airtel Money', logo: airtelMoneyLogo },
  afrimoney: { name: 'Afrimoney', logo: afrimoneyLogo },
};

/**
 * Retrouve l'opérateur depuis un code SerdiPay (MP, OM, AM, AF), un code
 * applicatif (orange_money, africell_money...) ou un libellé ('M-Pesa (Vodacom)',
 * 'Africell Money'), sans tenir compte de la casse. Inconnu : null.
 */
export function resolveOperator(value: unknown): OperatorKey | null {
  if (value === null || value === undefined) return null;
  const k = String(value).toLowerCase().replace(/[^a-z0-9]/g, '');
  if (!k) return null;
  if (k === 'mp' || k.includes('mpesa') || k.includes('vodacom')) return 'mpesa';
  if (k === 'om' || k.includes('orange')) return 'orange_money';
  if (k === 'am' || k.includes('airtel')) return 'airtel_money';
  if (k === 'af' || k.includes('afri')) return 'afrimoney';
  return null;
}

/** Nom affichable : celui de l'opérateur reconnu, sinon la valeur reçue. */
export function operatorName(value: unknown): string {
  const key = resolveOperator(value);
  if (key) return OPERATOR_INFO[key].name;
  return value === null || value === undefined ? '' : String(value);
}

export function OperatorLogo({ operator, size = 24, className = '', decorative = false }: {
  operator: unknown;
  size?: number;
  className?: string;
  /** Nom déjà visible à côté : logo ignoré par les lecteurs d'écran. */
  decorative?: boolean;
}) {
  const key = resolveOperator(operator);
  const style = { width: size, height: size, borderRadius: '20%' };
  if (!key) {
    const label = operatorName(operator) || 'Mobile Money';
    return (
      <span
        role={decorative ? undefined : 'img'}
        aria-label={decorative ? undefined : label}
        aria-hidden={decorative || undefined}
        data-operator="unknown"
        className={`inline-flex shrink-0 items-center justify-center border border-gray-200 bg-gray-100 text-gray-500 dark:border-gray-600 dark:bg-gray-700 dark:text-gray-300 ${className}`}
        style={style}
      >
        <Smartphone style={{ width: size * 0.6, height: size * 0.6 }} aria-hidden />
      </span>
    );
  }
  const info = OPERATOR_INFO[key];
  return (
    <img
      src={info.logo}
      alt={decorative ? '' : info.name}
      aria-hidden={decorative || undefined}
      width={size}
      height={size}
      loading="lazy"
      decoding="async"
      data-operator={key}
      className={`inline-block shrink-0 object-contain ${className}`}
      style={style}
    />
  );
}

/** Logo et nom côte à côte (récapitulatifs, reçus, historiques, listes). */
export function OperatorChip({ operator, size = 20, label, className = '', textClassName = 'text-sm' }: {
  operator: unknown;
  size?: number;
  /** Libellé à afficher à la place du nom de l'opérateur. */
  label?: string;
  /** Taille du texte (text-sm par défaut, text-xs dans les listes denses). */
  textClassName?: string;
  className?: string;
}) {
  const text = label ?? (operatorName(operator) || 'Mobile Money');
  return (
    <span className={`inline-flex min-w-0 max-w-full items-center gap-1.5 align-middle ${className}`}>
      <OperatorLogo operator={operator} size={size} decorative />
      <span className={`min-w-0 break-words ${textClassName}`}>{text}</span>
    </span>
  );
}
